import { useMemo } from 'react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

/**
 * Quick help options for Business List page
 * Provides guided tours for common actions
 */
export const useBusinessListQuickHelp = (pageType: 'member' | 'hrm') => {
    const isHRM = pageType === 'hrm'

    const options: QuickHelpOption[] = useMemo(() => {
        const memberOptions: QuickHelpOption[] = [
            {
                id: 'business-create',
                label: 'Tạo đơn công tác',
                description: 'Hướng dẫn tạo đơn công tác mới',
                selector: '[data-tour="create-business-btn"]',
                tourTitle: 'Tạo đơn công tác',
                tourDescription:
                    'Nhấn nút này để tạo đơn công tác mới.\n\n• Chọn loại công tác (trong nước/ngoài nước)\n• Chọn thời gian và địa điểm\n• Nhập mục đích công tác\n• Gửi đơn chờ phê duyệt\n\nĐơn sẽ được gửi đến trưởng phòng/HRM để xét duyệt.',
            },
            {
                id: 'business-cancel',
                label: 'Huỷ đơn công tác',
                description: 'Huỷ đơn đang chờ duyệt',
                selector: '[data-tour="business-data-table"]',
                tourTitle: 'Huỷ đơn công tác',
                tourDescription:
                    'Để huỷ đơn công tác:\n\n1. Tìm đơn có trạng thái "Đang chờ"\n2. Click vào icon hành động (⋮) ở cuối hàng\n3. Chọn "Huỷ"\n4. Xác nhận huỷ đơn\n\n**Lưu ý:**\n• Chỉ huỷ được đơn "Đang chờ" duyệt\n• Đơn đã huỷ không thể khôi phục',
                demo: 'business-cancel-action',
            },
            {
                id: 'business-view-stats',
                label: 'Xem thống kê công tác',
                description: 'Kiểm tra số ngày công tác',
                tourTitle: 'Thống kê công tác',
                tourDescription:
                    'Xem tổng quan công tác của bạn:\n\n• Tổng số ngày công tác trong năm\n• Số ngày công tác đã thực hiện\n• Số ngày công tác còn lại\n• Số đơn đang chờ duyệt\n\nThống kê được cập nhật theo thời gian thực.',
            },
        ]

        const hrmOptions: QuickHelpOption[] = [
            {
                id: 'business-approve',
                label: 'Duyệt đơn công tác',
                description: 'Phê duyệt hoặc từ chối đơn',
                tourTitle: 'Duyệt đơn công tác',
                tourDescription:
                    'Để duyệt đơn công tác:\n\n1. Click vào hàng đơn cần duyệt\n2. Xem chi tiết thông tin\n3. Click "Duyệt" hoặc "Từ chối"\n4. Nhập ghi chú (nếu cần)\n5. Xác nhận\n\nNhân viên sẽ nhận thông báo ngay.',
            },
            {
                id: 'business-bulk-approve',
                label: 'Duyệt nhiều đơn',
                description: 'Phê duyệt hàng loạt',
                selector: '[data-tour="business-data-table"]',
                tourTitle: 'Duyệt nhiều đơn cùng lúc',
                tourDescription:
                    'Quy trình duyệt nhiều đơn công tác:\n\n**Bước 1: Chọn phiếu**\nTick checkbox các phiếu cần duyệt. Chỉ phiếu "Đang chờ" hoặc "Leader đã duyệt" mới chọn được.\n\n**Bước 2: Bấm duyệt nhanh**\nThanh Selection hiện ra. Click "Duyệt công tác".\n\n**Bước 3: Xác nhận**\nModal hiện danh sách. Nhập phản hồi và xác nhận.',
                tourSteps: [
                    {
                        selector: '[data-tour="business-data-table"]',
                        title: 'Bước 1: Chọn phiếu công tác',
                        content:
                            'Tick vào checkbox đầu hàng để chọn các phiếu cần duyệt. Có thể chọn nhiều phiếu cùng lúc. Chỉ phiếu "Đang chờ" hoặc "Leader đã duyệt" mới có thể được chọn.',
                        demo: 'select-business-rows',
                    },
                    {
                        selector: '[data-tour="business-data-table"]',
                        title: 'Bước 2: Bấm duyệt nhanh',
                        content:
                            'Sau khi chọn phiếu, thanh Selection sẽ xuất hiện. Click vào nút "Duyệt công tác" để tiếp tục.',
                        demo: 'click-bulk-approve-business-button',
                    },
                    {
                        selector: '[data-tour="business-data-table"]',
                        title: 'Bước 3: Xem và xác nhận',
                        content:
                            'Modal hiện ra với danh sách đơn đã chọn. Kiểm tra thông tin, nhập phản hồi (nếu cần), rồi click "Xác nhận" để hoàn tất.',
                        demo: 'bulk-approve-business-modal',
                    },
                ],
            },
            {
                id: 'business-export',
                label: 'Xuất Excel',
                description: 'Export danh sách ra file',
                selector: '[data-tour="business-settings"]',
                tourTitle: 'Export danh sách',
                tourDescription:
                    'Xuất danh sách đơn công tác:\n\n• Click icon Settings (Settings)\n• Chọn "Export Excel"\n• File xuất theo bộ lọc hiện tại\n• Chứa đầy đủ thông tin đơn\n\nHữu ích cho báo cáo định kỳ.',
            },
        ]

        const commonOptions: QuickHelpOption[] = [
            {
                id: 'business-search',
                label: 'Tìm kiếm đơn',
                description: 'Tìm nhanh theo nhiều tiêu chí',
                selector: '[data-tour="business-search"]',
                tourTitle: 'Tìm kiếm đơn công tác',
                tourDescription:
                    'Tìm kiếm nhanh đơn công tác:\n\n• Tên nhân viên\n• Phòng ban\n• Loại công tác\n• Địa điểm\n\nKết quả hiển thị ngay khi gõ.',
            },
            {
                id: 'business-filter',
                label: 'Lọc danh sách',
                description: 'Sử dụng bộ lọc nâng cao',
                selector: '[data-tour="business-filter-bar"]',
                tourTitle: 'Bộ lọc đơn công tác',
                tourDescription:
                    'Lọc đơn công tác theo:\n\n• Trạng thái (chờ duyệt, đã duyệt, từ chối)\n• Loại công tác (trong nước, ngoài nước)\n• Khoảng thời gian\n• Phòng ban (HRM)\n• Nhân viên (HRM)\n\nKết hợp nhiều bộ lọc để tìm chính xác.',
            },
            {
                id: 'business-table-settings',
                label: 'Tuỳ chỉnh bảng',
                description: 'Cài đặt hiển thị bảng',
                selector: '[data-tour="business-settings"]',
                tourTitle: 'Cài đặt bảng',
                tourDescription: isHRM
                    ? 'Click icon Settings (Settings) để:\n\n• Điều chỉnh cỡ chữ\n• Thay đổi chiều cao hàng\n• Ẩn/hiện cột\n• Export Excel\n• Reset về mặc định\n\nCài đặt được lưu tự động.'
                    : 'Click icon Settings (Settings) để:\n\n• Điều chỉnh cỡ chữ\n• Thay đổi chiều cao hàng\n• Ẩn/hiện cột\n• Reset về mặc định\n\nCài đặt được lưu tự động.',
            },
            {
                id: 'business-overview-tour',
                label: 'Xem tour tổng quan',
                description: 'Hướng dẫn sử dụng trang',
                tourTitle: 'Tour hướng dẫn',
                tourDescription:
                    'Nhấn nút "Hướng dẫn sử dụng" ở góc phải màn hình để xem tour chi tiết các tính năng quản lý công tác.',
            },
        ]

        return isHRM
            ? [...hrmOptions, ...commonOptions]
            : [...memberOptions, ...commonOptions]
    }, [isHRM])

    useQuickHelp(options)

    return { options }
}

export default useBusinessListQuickHelp
